import { useState, useCallback, useRef } from 'react';
import { Message, Agent, ConversationMessage } from '../types';
import { apiService, ApiError, ClarificationState } from '../services/api';
import { normalizeProductAnchorsInContent, resolveProductDisplayKey } from '../utils/productAnchors';
import { appConfig } from '../config/appConfig';
// TEMP: Import applicationInsights for ChatResponse tracking
// This tracks the ACTUAL agent that handled the request (after routing)
import { applicationInsights } from '../services/applicationInsights';

// Static mapping of agent IDs to display names (fallback when agents list isn't available)
const AGENT_NAME_FALLBACK: Record<string, string> = {
  'aldo': 'Scout',
  'orientation': 'Scout',
  'orientation_agent': 'Scout',
  'orientation-agent': 'Scout',
  'hrpolicy': 'HR Policy',
  'pwms-support': 'PWMS Support',
  'product': 'Product',
  'direct_embroidery': 'Direct Embroidery'
};

export interface ToolCall {
  id: string;
  name: string;
  input: any;
  output?: any;
  status: 'calling' | 'completed' | 'error';
  timestamp: Date;
}





export const useChat = (
  sessionId: string,
  userId: string,
  onStatusMessage?: (message: string | null) => void,
  onAgentSwitch?: (newAgentName: string, toAgentId?: string, previousAgentName?: string) => void,
  availableAgents?: Agent[]
) => {
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState<string>('');
  const [toolCalls, setToolCalls] = useState<ToolCall[]>([]);
  const [currentToolCall, setCurrentToolCall] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Product displays can be either a products array (legacy) or an object with { products, heading, facets }
  const [productDisplays, setProductDisplays] = useState<Record<string, any>>({});
  const [alternativeDisplays, setAlternativeDisplays] = useState<Record<string, any>>({});
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const cancelSuppressUntilRef = useRef<number>(0);
  const currentStreamTokenRef = useRef<string | null>(null);
  
  // Clarification state - for hub-and-spoke clarification pattern
  const [clarificationState, setClarificationState] = useState<ClarificationState | null>(null);
  const [pendingClarification, setPendingClarification] = useState(false);

  // CRITICAL: Sync ref to track product displays for immediate capture
  // React state updates are async, so we need a ref to get the latest value synchronously
  const productDisplaysRef = useRef<Record<string, any>>({});

  // Buffer product_display events during streaming to avoid re-render thrash
  const pendingProductDisplaysRef = useRef<Record<string, any>>({});
  const productDisplayBatchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // GENERIC: Capture metadata from backend (sent in completion event)
  // Any agent can send metadata, the frontend just captures it
  const streamingMetadataRef = useRef<Record<string, any> | null>(null);
  
  // GENERIC: Capture filter alternatives from streaming (any agent can use)
  const filterAlternativesRef = useRef<any | null>(null);
  const routingMetadataRef = useRef<Record<string, any> | null>(null);
  
  // Track tool calls synchronously for message attachment (React state updates are async)
  const toolCallsRef = useRef<ToolCall[]>([]);

  // Generate unique message ID
  const generateMessageId = useCallback(() => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);






  // Convert UI Messages to API ConversationMessages (OpenAI format with rich metadata)
  // IMPORTANT: Include productDisplays, search_context, etc. for followup detection
  const convertMessagesToConversation = useCallback((messages: Message[]): ConversationMessage[] => {
    const converted = messages.map(msg => {
      const conversationMessage: ConversationMessage = {
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.content
      };
      
      // For agent messages, include rich metadata for Intent Agent followup detection
      if (msg.sender === 'agent') {
        // Include product displays (style codes, names for "them", "the first one", etc.)
        if ((msg as any).productDisplays && Object.keys((msg as any).productDisplays).length > 0) {
          (conversationMessage as any).product_displays = Object.entries((msg as any).productDisplays).map(
            ([anchorId, data]: [string, any]) => {
              const products = Array.isArray(data) ? data : (data?.products || []);
              return {
                anchor_id: anchorId,
                heading: data?.heading || '',
                products: products.slice(0, 12).map((p: any, idx: number) => ({
                  ordinal: idx + 1,
                  style_code: p?.attributes?.style_code || p?.base_sku || '',
                  name: p?.name || '',
                  category: p?.attributes?.sub_class_description || ''
                }))
              };
            }
          );
        }
        
        // Include search summary/metadata if available
        if ((msg as any).metadata?.orchestrator_trace?.search_summary) {
          const summary = (msg as any).metadata.orchestrator_trace.search_summary;
          (conversationMessage as any).search_context = {
            filters_applied: summary.filters_applied || [],
            result_count: summary.result_count || 0,
            category: summary.category || '',
            industry: summary.industry || ''
          };
        }
        
        // Include A2A routing info (which specialist handled the request)
        if ((msg as any).a2aRouting) {
          (conversationMessage as any).routed_agent = (msg as any).a2aRouting.currentAgent;
        }
      }
      
      // For user messages, mark if it was a followup
      if (msg.sender === 'user' && (msg as any).isFollowUp) {
        (conversationMessage as any).is_followup = true;
      }
      
      return conversationMessage;
    });
    return converted;
  }, []);

  // Convert API ConversationMessages to UI Messages (currently unused but kept for future use)
  // const convertConversationToMessages = useCallback((
  //   conversation: ConversationMessage[], 
  //   agentName?: string
  // ): Message[] => {
  //   return conversation.map((msg, index) => ({
  //     id: generateMessageId(),
  //     content: msg.content,
  //     sender: msg.role === 'user' ? 'user' : 'agent',
  //     timestamp: new Date(),
  //     ...(msg.role === 'assistant' && agentName && { agentName })
  //   }));
  // }, [generateMessageId]);

  // Removed proactive question detection - trusting Aldo's backend intelligence for routing decisions









  // Process response content for basic formatting
  const processResponseContent = useCallback((rawContent: string, citationSourceChunks?: any[]): string => {
    let processedContent = rawContent;

    // Basic content formatting for beautiful display
    // Agent now includes images directly in markdown format
    
    // Ensure proper markdown formatting is preserved
    // The response should already be well-formatted from the backend
    
    // Clean up trailing spaces/tabs at end of lines, but preserve newlines
    // Use [ \t] to match only spaces and tabs, not \n
    processedContent = processedContent.replace(/[ \t]+$/gm, '');
    
    return processedContent;
  }, []);

  // Process final content with citations (used at completion)
  const processStreamingContent = useCallback((
    rawContent: string,
    citationList?: string,
    citations?: any[]
  ): string => {
    // Base formatting
    let processedContent = processResponseContent(rawContent, citations);

    // NOTE: Agents are now responsible for explicitly including images in their responses
    // using markdown syntax: ![caption](url)
    // We do NOT automatically inject images from citations to avoid "leaked" images
    // that the agent didn't intend to show.

    return processedContent;
  }, [processResponseContent]);

  // Send message with streaming support
  const sendMessage = useCallback(async (
    content: string,
    agent: Agent,
    existingMessages: Message[],
    onConversationUpdate: (messages: Message[]) => void,
    streamingPreference: boolean = false,
    retryCount = 0,
    messageContext?: any
  ) => {
    // Send message start
    
    if (!agent || !content.trim()) {
      console.warn('❌ MESSAGE BLOCKED', { 
        noAgent: !agent, 
        emptyContent: !content.trim() 
      });
      return;
    }

    setError(null);
    setToolCalls([]);
    setCurrentToolCall(null);
    setCurrentStreamingMessage('');
    
    // CRITICAL: Ensure all completed messages have their productDisplays/filterAlternatives persisted before clearing global state
    // This prevents product displays and alternatives from disappearing when a new query is sent
    const messagesWithProductDisplays = existingMessages.map(msg => {
      // If this is a completed agent message, ensure it has all its display data attached
      if (msg.sender === 'agent' && msg.isCompleted) {
        let updated = msg;
        
        // Attach productDisplays if missing
        if (!msg.productDisplays) {
          const currentProductDisplays = productDisplaysRef.current;
          if (currentProductDisplays && Object.keys(currentProductDisplays).length > 0) {
            console.log('📦 Attaching productDisplays to completed message before clearing:', {
              messageId: msg.id,
              displayKeys: Object.keys(currentProductDisplays)
            });
            updated = { ...updated, productDisplays: { ...currentProductDisplays } };
          }
        }
        
        // Attach filterAlternatives if missing
        if (!updated.filterAlternatives && filterAlternativesRef.current) {
          console.log('🔍 Attaching filterAlternatives to completed message before clearing:', {
            messageId: msg.id,
            anchorId: filterAlternativesRef.current.anchorId
          });
          updated = { ...updated, filterAlternatives: filterAlternativesRef.current };
        }
        
        return updated;
      }
      return msg;
    });
    
    // DON'T clear product displays - with UUID anchor IDs, they won't collide
    // Each message gets its own unique anchors from the backend
    // This ensures displays persist across followup messages
    // Completed messages also have their own productDisplays snapshot attached above
    
    // Only clear pending buffer (not the accumulated displays)
    pendingProductDisplaysRef.current = {};
    filterAlternativesRef.current = null;
    streamingMetadataRef.current = null;
    routingMetadataRef.current = null;
    toolCallsRef.current = [];
    
    // DEBUG: Log that we're starting fresh for this message
    console.log('🔄 NEW MESSAGE - Tool calls reset:', {
      timestamp: new Date().toISOString(),
      messageContent: content.substring(0, 50),
      toolCallsRefLength: toolCallsRef.current.length,
      previousMessagesCount: existingMessages.length
    });

    // Add user message to conversation
    const userMessage: Message = {
      id: generateMessageId(),
      content: content.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    const updatedMessages = [...messagesWithProductDisplays, userMessage];
    onConversationUpdate(updatedMessages);

      // Note: Trusting Aldo's backend intelligence for routing decisions
      // A2A routing will be detected based on Aldo's response content, not frontend pattern matching
      
      // Add debug logging to analyze the handoff path
      const lastMessage = existingMessages[existingMessages.length - 1];
      const isFollowUpQuestion = existingMessages.length >= 2 && lastMessage?.sender === 'agent';
      // Fix TypeScript error by checking for all possible routing field names
      const lastAgentHadRouting = lastMessage?.a2aRouting?.currentAgent || 
                                 lastMessage?.a2aRouting?.originalAgent ||
                                 (lastMessage?.a2aRouting as any)?.finalAgent;
      
      // Debug log to confirm if follow-ups are using the wrong path
      // console.log('🔄 HANDOFF PATH ANALYSIS:', {
      //   isFollowUpQuestion,
      //   lastMessageSender: lastMessage?.sender,
      //   lastAgentHadRouting,
      //   lastAgentRouting: lastMessage?.a2aRouting,
      //   conversationLength: existingMessages.length,
      //   agentId: agent.id
      // });
      
      // Let the backend handle agent routing
      let targetAgent = agent;

    // Setting agent typing state
    setIsAgentTyping(true);

    try {
      const isFollowUp = existingMessages.length >= 1;
      // console.log('📤 useChat.sendMessage START', {
      //   agentId: agent?.id,
      //   agentName: agent?.name,
      //   isFollowUp,
      //   existingMessagesCount: existingMessages.length,
      //   sessionId,
      //   userId,
      //   productDisplaysKeys: Object.keys(productDisplays).length,
      //   pendingProductDisplaysKeys: Object.keys(pendingProductDisplaysRef.current || {}).length
      // });
      const conversationHistory = convertMessagesToConversation(updatedMessages);
      const shouldUseStreaming = streamingPreference;
      // Use targetAgent instead of agent for direct routing to specialized agents
      const isRealStreamingAgent = apiService.isRealStreamingAgent(targetAgent.id);

      // Force streaming for webapp agent since it always returns SSE
      if (isRealStreamingAgent) {
        // Handle real streaming for webapp agent
        // Using real streaming for agent
        
        // Create the agent message with appropriate initial content based on agent type
        let initialContent = 'Searching...';
        
        // Customize initial message based on agent type for better user experience
        if (agent.id === 'product' || agent.id.includes('product') || agent.id.includes('Product')) {
          initialContent = 'Searching product catalog...';
        } else if (agent.id === 'hrpolicy' || agent.id.includes('hr') || agent.id.includes('policy')) {
          initialContent = 'Searching policy documents...';
        } else if (agent.id === 'pwms-support' || agent.id.includes('pwms')) {
          initialContent = 'Searching PWMS documentation...';
        }
        
        const agentMessage: Message = {
          id: generateMessageId(),
          content: initialContent,
          sender: 'agent',
          timestamp: new Date(),
          agentName: agent.name,
          wasStreamed: true,
          isCompleted: false,
          isStatusMessage: true,
          // Add routing metadata for follow-up questions to preserve context
          ...(isFollowUpQuestion && lastAgentHadRouting && { a2aRouting: lastMessage?.a2aRouting })
        };

        // Add message to conversation immediately
        const messagesWithAgent = [...updatedMessages, agentMessage];
        onConversationUpdate(messagesWithAgent);

        // Track which message is currently streaming
        setStreamingMessageId(agentMessage.id);

        setIsStreaming(true);
        let fullResponse = '';
        let citationList = '';
        let citationSourceChunks: any[] = [];
        // Store A2A routing data locally to preserve it in final completion
        let a2aRoutingData: any = null;
        // Track tool calls made during A2A routing
        let a2aToolCalls: string[] = [];
        // Flag to track if we're in A2A mode (set when agent switch happens)
        let isA2AMode = false;
        // Preserve the routed agent name throughout streaming
        let routedAgentName: string | undefined = undefined;


        const streamToken = generateMessageId();
        currentStreamTokenRef.current = streamToken;
        let isStreamActive = true;
        
        // Reset metadata capture for this new message
        streamingMetadataRef.current = null;
        filterAlternativesRef.current = null;

        console.log('🎯 useChat: About to call API with agent:', {
          agentId: agent.id,
          agentName: agent.name,
          targetAgentId: targetAgent.id,
          isFollowUp,
          conversationLength: conversationHistory.length
        });
        
        await apiService.sendStreamingConversationMessage(
          agent.id, // Use the original agent
          conversationHistory,
          sessionId,
          async (event) => {
            // LOG EVERY EVENT to diagnose missing events issue
            console.log('🎯 EVENT IN useChat:', {
              type: (event as any)?.type,
              hasToolName: !!(event as any)?.tool_name,
              hasMetadata: !!(event as any)?.metadata,
              eventKeys: Object.keys(event || {})
            });
            
            if (!isStreamActive || currentStreamTokenRef.current !== streamToken) {
              console.log('⚠️ Event skipped - stream not active or token mismatch');
              return;
            }
            if ((event as any) && (event as any).type) {
              // light counter logging for first few events
            }
            // Handle content_final - replaces entire response with anchors included
            if ((event as any).type === 'content_final' && (event as any).content) {
              // Extract anchors from content_final
              const contentFinalAnchors = ((event as any).content.match(/\[(?:PRODUCTS|PRODUCT_CARDS):\s*([^\]]+)\]/g) || [])
                .map((m: string) => m.match(/\[(?:PRODUCTS|PRODUCT_CARDS):\s*([^\]]+)\]/)?.[1]);
              
              console.log('📝 Content Final Event - Replacing with anchors:', {
                newContentLength: (event as any).content.length,
                oldResponseLength: fullResponse.length,
                hasProductAnchors: /\[(?:PRODUCTS|PRODUCT_CARDS):/.test((event as any).content),
                anchorsInContentFinal: contentFinalAnchors,
                productDisplaysKeysBeforeUpdate: Object.keys(productDisplaysRef.current)
              });
              
              // REPLACE (not append) - this version has [PRODUCT_CARDS: anchor_id] markers
              fullResponse = (event as any).content;
              
              const processedContent = processResponseContent(fullResponse, citationSourceChunks);
              setCurrentStreamingMessage(processedContent);
            }
            
            if (event.type === 'response' && event.content) {
              // Track timing of content events vs agent switch events
              const hasProductAnchorInChunk = /\[(?:PRODUCTS|PRODUCT_CARDS):/.test(event.content);
              console.log('📝 Content Event Received:', {
                contentLength: event.content.length,
                fullResponseLength: fullResponse.length,
                hasExistingA2ARouting: !!agentMessage.a2aRouting,
                timestamp: Date.now(),
                hasProductAnchorInChunk
              });
              
              fullResponse += event.content;
              
              // Debug: Log what's actually being streamed
              console.log('📦 Raw chunk received:', JSON.stringify(event.content));
              console.log('📦 Full response so far:', fullResponse.slice(-100));
              console.log('🔍 Full response has product anchor:', /\[(?:PRODUCTS|PRODUCT_CARDS):/.test(fullResponse));
              
              // Process only the response content for formatting (no citations yet)
              const processedContent = processResponseContent(fullResponse, citationSourceChunks);
              console.log('🎨 Processed response content:', processedContent.slice(-100));
              
              setCurrentStreamingMessage(processedContent);
              
              // Clear tool call status and status message flag when response starts
              if (fullResponse.trim()) {
                setCurrentToolCall(null);
                // Clear the status message flag once real content arrives
                agentMessage.isStatusMessage = false;
              }
              
              // Note: Removed A2A content detection logic as it was causing routing conflicts.
              // The frontend now relies solely on backend agent_switch events for routing information.
              
              // Update the agent message in the conversation with processed content during streaming
              // Apply citations to message object for styling, but don't modify content during streaming
              let citations: any[] = [];
              if (citationSourceChunks && citationSourceChunks.length > 0) {
                citations = citationSourceChunks.map((chunk) => ({
                  citation_id: chunk.citation_id,
                  filename: chunk.filename || '',
                  page_number: chunk.page_number || 1,
                  chunk_index: chunk.chunk_index || 0,
                  content: chunk.content || '',
                  metadata: chunk.metadata || {},
                  search_score: chunk.search_score || 0,
                  citation: chunk.citation || ''
                }));
              }

              // Find the current state of the agent message (it might have been updated by agent_switch events)
              // NOTE: We need to be careful here - the conversation state might be behind the latest updates
              let currentAgentMessage = messagesWithAgent.find(msg => msg.id === agentMessage.id) || agentMessage;
              
              // If we have stored A2A routing data but the current message doesn't, use the stored data
              // This handles the case where the conversation state hasn't been updated yet with the latest routing
              if (a2aRoutingData && !currentAgentMessage.a2aRouting) {
                currentAgentMessage = { ...currentAgentMessage, a2aRouting: a2aRoutingData };
              }
              
              // Debug: Check the state of the current message (simplified)
              if (!currentAgentMessage.a2aRouting && a2aRoutingData) {
                // Log removed
}

              // Only replace thinking message when we have meaningful content
              // Keep the original thinking message if the processed content is empty or too short
              const shouldKeepThinkingMessage = !processedContent.trim() || processedContent.trim().length < 10;
              const displayContent = shouldKeepThinkingMessage ? currentAgentMessage.content : processedContent;

              // Defensive approach: once a message has a2aRouting, it should never lose it
              const preservedA2ARouting = a2aRoutingData || currentAgentMessage.a2aRouting || agentMessage.a2aRouting;
              // Defensive approach: preserve routed agent name once set
              // Priority: routedAgentName (from agent_switch event) > currentAgentMessage.agentName > a2aRouting.currentAgent > original agent
              // IMPORTANT: If we have a routed agent name, ALWAYS use it. Never fall back to original agent during routing.
              const preservedAgentName = routedAgentName || 
                                        currentAgentMessage.agentName || 
                                        (preservedA2ARouting?.currentAgent) || 
                                        agent.name;
              
              // Debug logging for agent name during streaming
              if (preservedA2ARouting && preservedAgentName !== preservedA2ARouting.currentAgent) {
                console.log('🎯 Agent name preservation during streaming:', {
                  messageId: agentMessage.id,
                  routedAgentName,
                  currentMessageAgentName: currentAgentMessage.agentName,
                  a2aCurrentAgent: preservedA2ARouting.currentAgent,
                  preservedAgentName,
                  originalAgent: agent.name
                });
              }
              
              let updatedAgentMessage = { 
                ...currentAgentMessage, 
                content: displayContent, 
                wasStreamed: true, 
                isCompleted: false,
                isStatusMessage: false, // Clear status message flag when real content arrives
                // Apply citations for styling during streaming, but don't modify content
                ...(citations.length > 0 && { citations }),
                ...(citationList && { citation_list: citationList }),
                // Defensive preservation: ensure a2aRouting is never lost once set
                ...(preservedA2ARouting && { a2aRouting: preservedA2ARouting }),
                // Defensive preservation: ensure routed agent name is ALWAYS set (even if it's the original agent)
                agentName: preservedAgentName
              };
              
              // Debug: Log status message flag clearing
              if (currentAgentMessage.isStatusMessage) {
                console.log('🔄 Clearing isStatusMessage flag for agent message', {
                  messageId: updatedAgentMessage.id,
                  wasStatus: currentAgentMessage.isStatusMessage,
                  nowStatus: updatedAgentMessage.isStatusMessage,
                  content: displayContent.substring(0, 50)
                });
              }
              
              // Debug routing preservation (minimal)
              if (!preservedA2ARouting) {
                // Log removed
}
              
              // Note: Removed hardcoded content analysis logic that was overriding backend routing decisions.
              // The frontend should only trust agent_switch events from the backend, not analyze content
              // to make its own routing decisions. This was causing incorrect routing display.
              
              // Update messages with any current A2A routing information (from backend events only)
              
              const updatedConversation = messagesWithAgent.map(msg => 
                msg.id === updatedAgentMessage.id ? updatedAgentMessage : msg
              );
              
              onConversationUpdate(updatedConversation);
              
              // Minimal streaming debug (comment out to disable completely)
              // // Log removed
            } else if (event.type === 'agent_switch') {
              // Handle A2A agent switching - this confirms our routing worked
              console.log('🔍 RAW agent_switch event from backend:', JSON.stringify(event, null, 2));
              const toAgentId = event.to_agent as string;
              const previousAgentName = event.previous_agent || event.from_agent;
              console.log('🎯 Extracted from event:', { toAgentId, previousAgentName, hasToAgent: !!event.to_agent, eventKeys: Object.keys(event) });
              
              // Look up the target agent's display name with robust fallback
              // 1. Use agent_name from backend if provided (most reliable)
              // 2. Try to find in available agents list (from agents.json)
              // 3. Fall back to static mapping if agent list lookup fails
              // 4. Last resort: use the agent ID itself
              const targetAgent = availableAgents?.find(a => a.id === toAgentId);
              const fallbackName = AGENT_NAME_FALLBACK[toAgentId];
              const newAgentName = event.agent_name || targetAgent?.name || fallbackName || toAgentId;
            const isOrientationAgent = typeof toAgentId === 'string' && toAgentId.toLowerCase().includes('orientation');
              
              // Track timing of agent switch events
              console.log('🔄 Agent Switch Event Received:', {
                toAgentId,
                targetAgent: targetAgent?.name,
                fallbackName,
                newAgentName,
                previousAgentName,
                availableAgentsCount: availableAgents?.length || 0,
                fullResponseLength: fullResponse.length,
                timestamp: Date.now()
              });
              
            if (isOrientationAgent) {
              console.log('🤫 Orientation routing detected - keeping Aldo visible', {
                toAgentId,
                previousAgentName,
                originalAgent: agent.name
              });
              // Keep the UI on Aldo, but preserve routing metadata for analytics.
              // This ensures ChatResponse events capture the handling agent.
              if (!routingMetadataRef.current) {
                routingMetadataRef.current = {
                  selected_agent_id: toAgentId,
                  selected_agent_name: newAgentName,
                  strategy: 'transparent_orientation',
                  mode: 'mainline'
                };
              }
              // Also set a2aRoutingData for consistency with other handoffs
              // This ensures the finalAgentMessage has routing info for ChatResponse tracking
              if (!a2aRoutingData) {
                a2aRoutingData = {
                  originalAgent: agent.name,
                  currentAgent: newAgentName,
                  previousAgent: previousAgentName
                };
              }
              return;
            }

              if (newAgentName) {
                // Notify consumer so follow-ups can be routed to the same agent
                try { onAgentSwitch && onAgentSwitch(newAgentName as string, toAgentId, previousAgentName); } catch {}
                // Log removed
// Set A2A mode flag to capture subsequent tool calls
                isA2AMode = true;
                
                // We don't need to add initial content here - the routing metadata is enough
                // The existing agent message will be updated with routing info, which will display the routing indicator
                
                // Log removed
// Status messages are disabled - skip status message logic
                // Log removed
// Update the agent message to show the switch
                // Always add A2A routing metadata for Aldo agent to show routing indicators
                // Always show the initial response immediately, even if it's just the thinking message
                // This ensures the agent routing indicator appears immediately
                let switchMessage = fullResponse.length > 0 ? fullResponse : agentMessage.content;
                
                // Always add A2A routing metadata for Aldo agent (agent.id === 'aldo')
                if (agent.id === 'aldo') {
                  // Normal A2A routing - add routing metadata
                  const routingData = {
                    originalAgent: agent.name,
                    currentAgent: newAgentName,
                    previousAgent: previousAgentName
                  };
                  
                  const updatedAgentMessage = { 
                    ...agentMessage, 
                    content: switchMessage, 
                    wasStreamed: true, 
                    isCompleted: false,
                    agentName: agent.name,
                    a2aRouting: routingData
                  };
                  
                  // Store A2A routing data and agent name locally to preserve throughout streaming
                  a2aRoutingData = routingData;
                  routedAgentName = newAgentName;
                  
                  // Debug logging for A2A routing addition
                  console.log('🔄 A2A Agent Switch - Adding Routing Metadata:', {
                    messageId: agentMessage.id,
                    originalMessageHadA2ARouting: !!agentMessage.a2aRouting,
                    newA2ARouting: updatedAgentMessage.a2aRouting,
                    storedA2ARoutingData: a2aRoutingData,
                    messageContent: switchMessage.substring(0, 50) + '...',
                    messagesBeforeUpdate: updatedMessages.length
                  });
                  
                  const updatedConversation = [...updatedMessages, updatedAgentMessage];
                  onConversationUpdate(updatedConversation);
                  
                  // Debug logging after conversation update
                  // Log removed
} else {
                  // Not Aldo agent - just update content without A2A metadata
                  // Log removed
const updatedAgentMessage = { 
                    ...agentMessage, 
                    content: switchMessage, 
                    wasStreamed: true, 
                    isCompleted: false,
                    agentName: agent.name // Keep original agent name instead of target agent
                    // No a2aRouting metadata
                  };
                  const updatedConversation = [...updatedMessages, updatedAgentMessage];
                  onConversationUpdate(updatedConversation);
                }
              }
            } else if (event.type === 'routing_metadata' && event.routing) {
              routingMetadataRef.current = event.routing;
              console.log('📡 Routing metadata received:', event.routing);
            } else if (event.type === 'tool_call' && event.tool_name) {
              // Track the current tool being called
              setCurrentToolCall(event.tool_name);
              
              // Generate a unique ID for this specific tool call instance
              const toolCallId = `tool_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              
              console.log('🔧 TOOL_CALL EVENT RECEIVED in useChat:', {
                tool_name: event.tool_name,
                tool_input: event.tool_input,
                toolCallId,
                isA2AMode,
                currentToolCallsRefLength: toolCallsRef.current.length,
                existingCallsForTool: toolCallsRef.current.filter(tc => tc.name === event.tool_name).length
              });
              
              // Check if we already have a 'calling' entry for this exact tool
              // This prevents duplicate tool_call events from creating multiple entries
              const existingCallingTool = toolCallsRef.current.find(
                tc => tc.name === event.tool_name && tc.status === 'calling'
              );
              
              if (existingCallingTool) {
                console.log('🔧 Skipping duplicate tool_call - already tracking:', event.tool_name);
              } else {
                // Always track tool calls for query details (both A2A and direct)
                const newToolCall: ToolCall = {
                  id: toolCallId,
                  name: event.tool_name,
                  input: event.tool_input || (event as any).arguments || {},
                  status: 'calling',
                  timestamp: new Date()
                };
                setToolCalls(prev => [...prev, newToolCall]);
                // Also update ref synchronously for capture at completion
                toolCallsRef.current = [...toolCallsRef.current, newToolCall];
                console.log('🔧 Tool call added to ref:', { 
                  tool: newToolCall.name, 
                  id: newToolCall.id,
                  refLength: toolCallsRef.current.length, 
                  isA2AMode 
                });
              }
              
              // Additionally track in A2A list for routing display
              if (isA2AMode) {
                if (!a2aToolCalls.includes(event.tool_name)) {
                  a2aToolCalls.push(event.tool_name);
                  console.log('🔧 A2A tool call captured (tool_call event):', event.tool_name);
                }
              }
            } else if (event.type === 'status') {
              // Handle dynamic status updates from all agents (generic handling)
              if (event.status && typeof event.status === 'string') {
                // Use event.message if available (user-friendly), otherwise fallback to event.status
                const displayMessage = event.message || event.status;
                
                // Skip internal status messages (like 'agent_updated', 'tool_completed') - don't display or update message content
                if (event.status !== 'agent_updated' && event.status !== 'tool_completed') {
                  // Display status message to user
                  if (onStatusMessage) {
                    onStatusMessage(displayMessage);
                  }
                  
                  // Update the agent message content to show current status
                  const currentMessages = [...messagesWithAgent];
                  const lastMessage = currentMessages[currentMessages.length - 1];
                  
                  if (lastMessage && lastMessage.id === agentMessage.id && !lastMessage.isCompleted) {
                    lastMessage.content = displayMessage;
                    lastMessage.isStatusMessage = true;
                    lastMessage.agentName = agent.name;
                    onConversationUpdate(currentMessages);
                    
                    console.log('✅ Status message updated in conversation:', {
                      messageId: lastMessage.id,
                      isStatusMessage: lastMessage.isStatusMessage,
                      agentName: lastMessage.agentName,
                      content: displayMessage,
                      routedAgentName: routedAgentName,
                      a2aCurrentAgent: a2aRoutingData?.currentAgent,
                      lastMessageA2AAgent: lastMessage.a2aRouting?.currentAgent,
                      originalAgent: agent.name
                    });
                  }
                } else {
                  // Log agent_updated events for debugging but don't display
                  console.log('Skipping agent_updated status display');
                }
              }
            } else if (event.type === 'tool_call') {
              // Backup handler - should rarely fire since primary handler above catches tool_call events
              // This is kept for edge cases where events might slip through
              console.log('🔧 Tool call (backup handler check):', { 
                tool_name: event.tool_name,
                alreadyTracking: toolCallsRef.current.some(tc => tc.name === event.tool_name && tc.status === 'calling')
              });
              // Skip - primary handler should have caught this
            } else if (event.type === 'tool_output') {
              // Handle tool output events - update the tool call with output (includes timing data)
              const toolName = event.tool_name;
              const toolOutput = event.tool_output;
              
              console.log('🔧 TOOL_OUTPUT processing:', { 
                toolName, 
                hasOutput: !!toolOutput,
                outputType: typeof toolOutput,
                outputPreview: typeof toolOutput === 'string' ? toolOutput.substring(0, 100) : JSON.stringify(toolOutput)?.substring(0, 100),
                currentToolCallsCount: toolCallsRef.current.length,
                toolsWithCallingStatus: toolCallsRef.current.filter(tc => tc.status === 'calling').map(tc => tc.name),
                allTools: toolCallsRef.current.map(tc => ({ name: tc.name, status: tc.status, hasOutput: !!tc.output }))
              });
              
              // Clear current tool call indicator since we got output
              setCurrentToolCall(null);
              
              if (toolOutput) {
                // STRATEGY: Find the best tool to attach this output to
                // Priority 1: Tool with matching name that is still 'calling'
                // Priority 2: Tool with matching name that doesn't have output yet
                // Priority 3: ANY tool that is still 'calling' (backend may have sent wrong name)
                // Priority 4: ANY tool without output (fallback for out-of-order events)
                
                let targetToolId: string | null = null;
                
                // Priority 1: Exact match with 'calling' status
                const exactCallingMatch = toolCallsRef.current.find(
                  tc => tc.name === toolName && tc.status === 'calling'
                );
                if (exactCallingMatch) {
                  targetToolId = exactCallingMatch.id;
                  console.log('🔧 Tool output match: exact name + calling status');
                }
                
                // Priority 2: Exact name match without output
                if (!targetToolId) {
                  const exactNoOutput = toolCallsRef.current.find(
                    tc => tc.name === toolName && !tc.output
                  );
                  if (exactNoOutput) {
                    targetToolId = exactNoOutput.id;
                    console.log('🔧 Tool output match: exact name, no output yet');
                  }
                }
                
                // Priority 3: ANY tool still calling (backend sent wrong name)
                if (!targetToolId) {
                  const anyCallingTool = toolCallsRef.current.find(tc => tc.status === 'calling');
                  if (anyCallingTool) {
                    targetToolId = anyCallingTool.id;
                    console.log('🔧 Tool output match: fallback to any calling tool (backend name mismatch)', {
                      expectedName: toolName,
                      actualToolName: anyCallingTool.name
                    });
                  }
                }
                
                // Priority 4: ANY tool without output
                if (!targetToolId) {
                  const anyWithoutOutput = toolCallsRef.current.find(tc => !tc.output);
                  if (anyWithoutOutput) {
                    targetToolId = anyWithoutOutput.id;
                    console.log('🔧 Tool output match: fallback to any tool without output', {
                      expectedName: toolName,
                      actualToolName: anyWithoutOutput.name
                    });
                  }
                }
                
                if (targetToolId) {
                  // Update the matched tool
                  toolCallsRef.current = toolCallsRef.current.map(tc => {
                    if (tc.id === targetToolId) {
                      return { ...tc, output: toolOutput, status: 'completed' as const };
                    }
                    return tc;
                  });
                  setToolCalls(prev => prev.map(tc => {
                    if (tc.id === targetToolId) {
                      return { ...tc, output: toolOutput, status: 'completed' as const };
                    }
                    return tc;
                  }));
                  console.log('🔧 Tool output applied:', { 
                    targetToolId,
                    allToolStates: toolCallsRef.current.map(tc => ({ name: tc.name, status: tc.status, hasOutput: !!tc.output }))
                  });
                } else {
                  console.warn('🔧 No tool found to attach output to:', { 
                    toolName, 
                    availableTools: toolCallsRef.current.map(tc => ({ name: tc.name, status: tc.status, hasOutput: !!tc.output }))
                  });
                }
              }
            } else if (event.type === 'citation_list' && event.citation_list) {
              // Accumulate citation list but don't apply to content yet
              citationList = event.citation_list;
              console.log('🔖 Citation list received:', {
                agentId: agent.id,
                agentName: agent.name,
                citationListLength: citationList?.length,
                citationListPreview: citationList?.substring(0, 200),
                hasCitations: Array.isArray(event.citations),
                citationsCount: Array.isArray(event.citations) ? event.citations.length : 0,
                firstCitation: event.citations?.[0] ? {
                  id: event.citations[0].citation_id,
                  filename: event.citations[0].filename
                } : 'none'
              });
            } else if (event.type === 'citation_source_chunks' && event.source_chunks) {
              // Accumulate citation source chunks 
              citationSourceChunks = event.source_chunks;
              console.log('📚 Citation source chunks received:', {
                agentId: agent.id,
                agentName: agent.name,
                chunkCount: citationSourceChunks?.length,
                firstChunk: citationSourceChunks?.[0] ? {
                  citation_id: citationSourceChunks[0].citation_id,
                  filename: citationSourceChunks[0].filename,
                  contentPreview: citationSourceChunks[0].content?.substring(0, 50)
                } : 'none',
                chunks: citationSourceChunks?.map(c => ({
                  citation_id: c.citation_id,
                  filename: c.filename,
                  hasMetadata: !!c.metadata,
                  metadataKeys: c.metadata ? Object.keys(c.metadata) : []
                }))
              });
            } else if (event.type === 'product_display' && event.products) {
              const anchorId = event.anchor_id || `anchor_${Date.now()}`;
              const products = event.products || [];
              const heading = (event as any).heading || '';
              const facets = (event as any).facets || [];
              
              console.log('🎯 PRODUCT_DISPLAY EVENT RECEIVED:', {
                anchorId,
                productCount: products.length,
                facetCount: facets.length,
                heading,
                facetNames: facets.map((f: any) => f.field_name || f.name),
                existingKeys: Object.keys(productDisplaysRef.current)
              });
              
              // Single event with all products (batching removed for simplicity)
              console.log('🔵 FRONTEND RECEIVED FROM BACKEND (using backend order directly - NO SORT):', {
                anchorId,
                productCount: products.length,
                heading,
                facetCount: facets.length,
                firstProduct: products[0]?.name?.substring(0, 50) || 'N/A'
              });
              products.slice(0, 10).forEach((p: any, idx: number) => {
                const styleCode = p?.attributes?.style_code || 'UNKNOWN';
                const name = p?.name || '';
                const isUniFirst = p?.attributes?.made_by_unifirst || false;
                const isCurated = p?.is_curated_related || false;
                const isSemantic = p?.is_semantic_alternative || false;
                const score = p?.score?.overall_score || p?.score?.fit || 0;
                const flag = isUniFirst ? '✅' : '❌';
                const curatedBadge = isCurated ? '🏷️CURATED' : '';
                const semanticBadge = isSemantic ? '🔍AI' : '';
                console.log(`  #${idx+1}: ${styleCode} ${flag} ${curatedBadge} ${semanticBadge} score=${score.toFixed(2)} - ${name.substring(0, 40)}`);
              });

              // Update product displays (no batching delay)
              // NO SORTING! Backend already sorted products correctly
              // Store full display data including facets and heading
              
              // CRITICAL FIX: Update ref SYNCHRONOUSLY BEFORE setState to prevent race condition
              // React may batch setState callbacks, so the ref must be updated immediately
              // so that when onStreamComplete captures messageProductDisplays, all displays are present
              const newDisplayData = {
                products,
                heading,
                facets
              };
              
              // Update ref SYNCHRONOUSLY (outside of setState callback)
              productDisplaysRef.current = {
                ...productDisplaysRef.current,
                [anchorId]: newDisplayData
              };
              
              console.log('✅ PRODUCT DISPLAYS UPDATED (ref sync):', {
                anchorId,
                productCount: products.length,
                heading,
                facetCount: facets.length,
                allAnchors: Object.keys(productDisplaysRef.current)
              });
              
              // Also update React state (for component re-renders)
              setProductDisplays(prev => ({
                ...prev,
                [anchorId]: newDisplayData
              }));
            } else if (event.type === 'filter_alternatives' && event.alternatives) {
              // GENERIC: Handle filter alternatives (clickable options for any agent)
              console.log('🔍 Received filter_alternatives event:', event);
              
              const anchorId = event.anchor_id || '';
              const filterAlternatives = {
                anchorId: anchorId,
                unmappedFilter: event.unmapped_filter || '',
                unmappedValue: event.unmapped_value || '',
                alternatives: event.alternatives || []
              };
              
              // Store in ref to attach to message after streaming completes
              filterAlternativesRef.current = filterAlternatives;
              
              // ALSO store by anchor_id for marker replacement (like product cards)
              if (anchorId) {
                setAlternativeDisplays(prev => ({
                  ...prev,
                  [anchorId]: filterAlternatives
                }));
                console.log('✅ Stored alternatives with anchor_id:', anchorId);
              }
              
              console.log('✅ Stored filter alternatives:', {
                anchorId,
                alternativeCount: filterAlternatives.alternatives.length
              });
            } else if (event.type === 'clarification_needed') {
              // Handle clarification request from backend (hub-and-spoke pattern)
              console.log('🤔 Clarification needed event:', event);
              
              const newClarificationState: ClarificationState = {
                pending: true,
                stage: event.stage || 'unknown',
                question: event.question || 'Could you provide more details?',
                options: event.options || [],
                ambiguities: event.ambiguities || [],
                sessionId: sessionId
              };
              
              setClarificationState(newClarificationState);
              setPendingClarification(true);
              
              // Show the clarification question as a message
              const clarificationContent = event.question || 'I need a bit more information to help you.';
              fullResponse = clarificationContent;
              setCurrentStreamingMessage(clarificationContent);
              
              console.log('✅ Clarification state set:', {
                stage: newClarificationState.stage,
                question: newClarificationState.question,
                optionsCount: newClarificationState.options.length,
                ambiguitiesCount: newClarificationState.ambiguities?.length || 0
              });
            } else if (event.type === 'done') {
              // Log the FULL done event to see what backend sends
              console.log('📬 DONE EVENT RECEIVED:', {
                hasMetadata: !!event.metadata,
                metadata: event.metadata,
                toolsCalled: (event as any).tools_called,
                toolCount: (event as any).tool_count,
                durationMs: (event as any).duration_ms,
                fullEvent: event
              });
              
              if (event.metadata) {
              // GENERIC: Capture metadata from backend completion event
              // Any agent can send metadata, the frontend just captures it
              streamingMetadataRef.current = event.metadata;
              
              // IMPORTANT: Use tool_details from done event as backup/supplement for timing data
              // This ensures we capture timing even if tool_output events were missed
              const toolDetails = event.metadata?.tool_details;
              if (toolDetails && Array.isArray(toolDetails) && toolDetails.length > 0) {
                console.log('🔧 Tool details from done event:', {
                  toolCount: toolDetails.length,
                  currentTrackedCount: toolCallsRef.current.length,
                  trackedTools: toolCallsRef.current.map(tc => tc.name),
                  tools: toolDetails.map((t: any) => ({ 
                    tool: t.tool, 
                    hasTiming: !!t.timing,
                    timing: t.timing 
                  }))
                });
                
                // First: Update existing tool calls with timing data
                toolCallsRef.current = toolCallsRef.current.map(tc => {
                  if (!tc.output || (typeof tc.output === 'string' && !tc.output.includes('_timing'))) {
                    // Find matching tool detail with timing
                    const detail = toolDetails.find((td: any) => td.tool === tc.name);
                    if (detail?.timing) {
                      console.log('🔧 Merging timing from done event for:', tc.name);
                      return {
                        ...tc,
                        output: JSON.stringify({ _timing: detail.timing }),
                        status: 'completed' as const
                      };
                    }
                  }
                  return tc;
                });
                
                // Second: Add any tools from done event that weren't tracked (missed tool_call events)
                const trackedToolNames = toolCallsRef.current.map(tc => tc.name);
                const missingTools = toolDetails.filter((td: any) => !trackedToolNames.includes(td.tool));
                
                if (missingTools.length > 0) {
                  console.log('🔧 Adding missing tools from done event:', missingTools.map((t: any) => t.tool));
                  
                  const newTools = missingTools.map((td: any) => ({
                    id: `tool_done_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    name: td.tool,
                    input: td.arguments || {},
                    output: td.timing ? JSON.stringify({ _timing: td.timing }) : undefined,
                    status: 'completed' as const,
                    timestamp: new Date()
                  }));
                  
                  toolCallsRef.current = [...toolCallsRef.current, ...newTools];
                }
                
                // Also update React state
                setToolCalls(prev => {
                  let updated = prev.map(tc => {
                    if (!tc.output || (typeof tc.output === 'string' && !tc.output.includes('_timing'))) {
                      const detail = toolDetails.find((td: any) => td.tool === tc.name);
                      if (detail?.timing) {
                        return {
                          ...tc,
                          output: JSON.stringify({ _timing: detail.timing }),
                          status: 'completed' as const
                        };
                      }
                    }
                    return tc;
                  });
                  
                  // Add missing tools
                  const trackedNames = prev.map(tc => tc.name);
                  const missing = toolDetails.filter((td: any) => !trackedNames.includes(td.tool));
                  
                  if (missing.length > 0) {
                    const newTools = missing.map((td: any) => ({
                      id: `tool_done_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                      name: td.tool,
                      input: td.arguments || {},
                      output: td.timing ? JSON.stringify({ _timing: td.timing }) : undefined,
                      status: 'completed' as const,
                      timestamp: new Date()
                    }));
                    updated = [...updated, ...newTools];
                  }
                  
                  return updated;
                });
              }
              
              console.log('📊 Captured metadata from backend:', {
                hasMetadata: !!event.metadata,
                metadataKeys: Object.keys(event.metadata || {}),
                finalToolCount: toolCallsRef.current.length,
                finalTools: toolCallsRef.current.map(tc => ({ name: tc.name, hasOutput: !!tc.output }))
              });
              } // Close if (event.metadata)
            } else if (event.type === 'error') {
              // Backend sent an explicit stream error (e.g. agent-service down,
              // upstream timeout). Previously silently dropped here, leaving the
              // UI stuck with no content and no indication anything failed.
              const backendError = (event as any).error || 'The assistant ran into a problem generating a response.';
              const backendRequestId = (event as any).requestId;
              console.error('❌ SSE stream error event:', { error: backendError, requestId: backendRequestId, event });
              setError(backendRequestId ? `${backendError} (ref: ${backendRequestId.slice(0, 8)})` : backendError);
              setIsStreaming(false);
              setCurrentStreamingMessage('');
              setStreamingMessageId(null);
              currentStreamTokenRef.current = null;
              isStreamActive = false;
            }
          },
          (finalResponse) => {
            if (!isStreamActive || currentStreamTokenRef.current !== streamToken) {
              return;
            }
            // Since we're processing content in real-time, just complete the streaming
            setIsStreaming(false);
            setCurrentStreamingMessage('');
            console.log('✅ useChat.sendMessage COMPLETE', {
              contentLength: finalResponse?.length || 0
            });
            setStreamingMessageId(null);
            currentStreamTokenRef.current = null;
            isStreamActive = false;

            // Apply any buffered product displays immediately when stream ends
            try {
              if (pendingProductDisplaysRef.current && Object.keys(pendingProductDisplaysRef.current).length > 0) {
                setProductDisplays(prev => {
                  const updated = { ...prev, ...pendingProductDisplaysRef.current };
                  productDisplaysRef.current = updated; // Update ref synchronously
                  return updated;
                });
                pendingProductDisplaysRef.current = {};
              }
              if (productDisplayBatchTimeout.current) {
                clearTimeout(productDisplayBatchTimeout.current);
                productDisplayBatchTimeout.current = null;
              }
            } catch {}
            
            // Citations are already applied during streaming, so just mark as completed
            // No setTimeout needed - this prevents the scroll jump issue
            
            // Convert citation source chunks to Citation objects if not already done
            let citations: any[] = [];
            if (citationSourceChunks && citationSourceChunks.length > 0) {
              // Log removed
citations = citationSourceChunks.map((chunk) => ({
                citation_id: chunk.citation_id,
                filename: chunk.filename || '',
                page_number: chunk.page_number || 1,
                chunk_index: chunk.chunk_index || 0,
                content: chunk.content || '',
                source_url: chunk.source_url || '',
                metadata: chunk.metadata || {},
                search_score: chunk.search_score || 0,
                citation: chunk.citation || '',
                image_ids: chunk.image_ids || [],
                image_urls: chunk.image_urls || [],
                image_captions: chunk.image_captions || [],
                image_formats: chunk.image_formats || [],
                image_sizes: chunk.image_sizes || []
              }));
              
              console.log('✅ Converted citations:', {
                agentId: agent.id,
                convertedCount: citations.length,
                citationsPreview: citations.map(c => ({
                  id: c.citation_id,
                  filename: c.filename,
                  page: c.page_number
                }))
              });
            } else {
              // Log removed
}
            
            // Process final content with citations consistently
            // IMPORTANT: Do NOT rely on captured currentStreamingMessage (can be stale across renders)
            // Prefer locally accumulated fullResponse from this stream
            const safeFinalText = (fullResponse && fullResponse.length > 0)
              ? fullResponse
              : (finalResponse || '');
            
            // Debug: Log citation processing inputs
            const hasProductAnchorInSafe = /\[(?:PRODUCTS|PRODUCT_CARDS):/.test(safeFinalText);
            const productAnchorInSafe = safeFinalText.match(/\[(?:PRODUCTS|PRODUCT_CARDS):\s*([^\]]+)\]/);
            console.log('🔗 Processing citations on completion:', {
              agentId: agent.id,
              citationsCount: citations.length,
              hasCitationList: !!citationList,
              contentLength: safeFinalText.length,
              contentPreview: safeFinalText.substring(0, 200),
              citationIds: citations.map(c => c.citation_id),
              hasProductAnchorInSafe,
              productAnchorInSafe: productAnchorInSafe ? productAnchorInSafe[0] : null
            });
            
            // NO SORTING! Backend already sorted products correctly
            // Products are already in the correct order from the ranker

            const finalContent = processStreamingContent(safeFinalText, citationList, citations);

            // Repair LLM markers like [PRODUCTS: 30] (product count) → [PRODUCT_CARDS: pd_xxx]
            const repairedContent = normalizeProductAnchorsInContent(
              finalContent,
              productDisplaysRef.current
            );
            
            // Debug: Check if citations were replaced
            const hasCitationMarkers = /\[\d+\]/.test(safeFinalText);
            const stillHasCitationMarkers = /\[\d+\]/.test(finalContent);
            const hasProductAnchor = /\[(?:PRODUCTS|PRODUCT_CARDS):/.test(finalContent);
            const productAnchorMatch = finalContent.match(/\[(?:PRODUCTS|PRODUCT_CARDS):\s*([^\]]+)\]/);
            const hasAlternativesAnchor = finalContent.includes('[ALTERNATIVES:');
            const alternativesAnchorMatch = finalContent.match(/\[ALTERNATIVES:\s*([^\]]+)\]/g);
            console.log('🔗 Citation replacement result:', {
              agentId: agent.id,
              hadMarkers: hasCitationMarkers,
              stillHasMarkers: stillHasCitationMarkers,
              markersRemoved: hasCitationMarkers && !stillHasCitationMarkers,
              finalContentPreview: finalContent.substring(0, 200),
              finalContentLength: finalContent.length,
              hasProductAnchor,
              productAnchorMatch: productAnchorMatch ? productAnchorMatch[0] : null,
              hasAlternativesAnchor,
              alternativesAnchorMatch: alternativesAnchorMatch || [],
              alternativesCount: alternativesAnchorMatch ? alternativesAnchorMatch.length : 0
            });
            
            // NO SORTING! Backend already sorted products correctly
            // Products are already in the correct order from the ranker
            
            // Determine final agent name - always present Aldo as responder
            const finalAgentName = agent.name;
            
            // Determine product card display mode for this message
            // Check if message has product cards and calculate mode based on product count
            let productCardMode: 'detailed' | 'standard' | undefined = undefined;
            const hasProductCards = /\[(?:PRODUCTS|PRODUCT_CARDS):/.test(repairedContent);
            if (hasProductCards) {
              // Extract all product card anchors from the message
              const anchorRegex = /\[(?:PRODUCTS|PRODUCT_CARDS):\s*([^\]]+)\]/g;
              let totalProductCount = 0;
              let match;
              const anchorsFound: string[] = [];
              const productsFound: any[] = [];
              
              while ((match = anchorRegex.exec(repairedContent)) !== null) {
                const anchor = match[1];
                anchorsFound.push(anchor);

                const resolvedKey = resolveProductDisplayKey(
                  anchor,
                  productDisplaysRef.current,
                  anchorsFound.length - 1
                );
                const displayData = resolvedKey
                  ? (productDisplaysRef.current as any)[resolvedKey]
                  : undefined;
                const products = Array.isArray(displayData)
                  ? displayData
                  : displayData?.products;
                if (products && Array.isArray(products)) {
                  totalProductCount += products.length;
                  productsFound.push(...products);
                  console.log(
                    `✅ Found ${products.length} products for anchor "${anchor}" via key "${resolvedKey}"`
                  );
                }
              }
              
              // Set display mode: detailed if 2 or fewer products total (as requested by user), standard otherwise
              // Only set if we actually found products, otherwise leave undefined for fallback logic
              if (totalProductCount > 0) {
                productCardMode = totalProductCount <= 2 ? 'detailed' : 'standard';
              }
              
              console.log('🎨 Product card display mode determined:', {
                messageId: agentMessage.id,
                anchorsFound,
                totalProductCount,
                displayMode: productCardMode,
                productDisplaysKeys: Object.keys(productDisplays),
                productsFoundCount: productsFound.length
              });
            }
            
            // CRITICAL: Capture product displays for THIS message only from REF (not state)
            // React state updates are async, so we use the ref to get the latest value synchronously
            // This prevents future messages from retroactively updating this message's products
            const messageProductDisplays = { ...productDisplaysRef.current };
            
            console.log('📦 Capturing product displays for message:', {
              messageId: agentMessage.id,
              capturedKeys: Object.keys(messageProductDisplays),
              capturedDetails: Object.entries(messageProductDisplays).map(([k, v]: [string, any]) => ({
                anchor: k,
                productCount: Array.isArray(v) ? v.length : v?.products?.length || 0,
                facetCount: v?.facets?.length || 0,
                heading: v?.heading || ''
              }))
            });
            
            // CLEAN: Use metadata from backend (sent in completion event)
            // Any agent can send metadata, the frontend just captures it
            const messageMetadata = streamingMetadataRef.current || {};
            
            if (messageMetadata && Object.keys(messageMetadata).length > 0) {
              console.log('📊 Using metadata from backend:', {
                metadataKeys: Object.keys(messageMetadata),
                productCount: messageMetadata.products?.count,
                skuCount: messageMetadata.products?.skus?.length,
                styleCount: messageMetadata.products?.styles?.length
              });
            }
            
            // GENERIC: Capture filter alternatives from streaming
            const messageFilterAlternatives = filterAlternativesRef.current;
            
            if (messageFilterAlternatives) {
              console.log('🔍 Attaching filter alternatives to message:', {
                messageId: agentMessage.id,
                alternativeCount: messageFilterAlternatives.alternatives?.length || 0
              });
            }
            
            // GENERIC: Extract search_summary from orchestrator_trace (product agent)
            const messageSearchSummary = messageMetadata.orchestrator_trace?.search_summary;
            
            if (messageSearchSummary) {
              console.log('📊 Attaching search_summary to message:', {
                messageId: agentMessage.id,
                eventCount: messageSearchSummary.events?.length || 0,
                title: messageSearchSummary.title
              });
            }
            
            // CLEANUP: Mark any remaining 'calling' tools as 'completed' 
            // This handles cases where tool_output events were missed
            const stillCallingTools = toolCallsRef.current.filter(tc => tc.status === 'calling');
            if (stillCallingTools.length > 0) {
              console.log('🔧 Cleaning up tools still marked as calling:', stillCallingTools.map(tc => tc.name));
              toolCallsRef.current = toolCallsRef.current.map(tc => {
                if (tc.status === 'calling') {
                  return { ...tc, status: 'completed' as const };
                }
                return tc;
              });
              setToolCalls(prev => prev.map(tc => {
                if (tc.status === 'calling') {
                  return { ...tc, status: 'completed' as const };
                }
                return tc;
              }));
            }
            
            // Capture tool call details for tracing
            const messageToolCallDetails = toolCallsRef.current.length > 0 ? [...toolCallsRef.current] : undefined;
            
            // DEBUG: Always log tool call capture state
            console.log('🔧 FINAL CAPTURE - Tool call details:', {
              messageId: agentMessage.id,
              toolCallsRefLength: toolCallsRef.current.length,
              hasToolCallDetails: !!messageToolCallDetails,
              toolCount: messageToolCallDetails?.length || 0,
              cleanedUpCallingTools: stillCallingTools.length,
              tools: messageToolCallDetails?.map(t => ({ 
                name: t.name, 
                status: t.status, 
                hasOutput: !!t.output,
                outputType: t.output ? typeof t.output : 'none',
                outputPreview: t.output ? (typeof t.output === 'string' ? t.output.substring(0, 50) : 'object') : 'none'
              })) || []
            });
            
            const finalAgentMessage = { 
              ...agentMessage, 
              content: repairedContent, 
              wasStreamed: true, 
              isCompleted: true,
              isStatusMessage: false, // CRITICAL: Clear status message flag on completion
              ...(citations.length > 0 && { citations }),
              // Only add citation_list if it wasn't already included in content
              ...(!repairedContent.toLowerCase().includes('sources') && citationList && { citation_list: citationList }),
              // Preserve A2A routing data - prioritize local variable, fallback to agent message
              a2aRouting: a2aRoutingData || agentMessage.a2aRouting,
              // ALWAYS set agent name (don't use conditional spread - ensure it's set even on completion)
              agentName: finalAgentName,
              // Store A2A tool calls for feedback
              ...(a2aToolCalls.length > 0 && { a2aToolCalls }),
              // Store tool call details with input/output for query tracing
              ...(messageToolCallDetails && { toolCallDetails: messageToolCallDetails }),
              // Store product card display mode for this message
              ...(productCardMode && { productCardMode }),
              // CRITICAL: Store product displays per-message to prevent retroactive updates
              productDisplays: messageProductDisplays,
              // Store generic metadata (product SKUs, etc.)
              ...(Object.keys(messageMetadata).length > 0 && { metadata: messageMetadata }),
              // GENERIC: Store filter alternatives (any agent can use)
              ...(messageFilterAlternatives && { filterAlternatives: messageFilterAlternatives }),
              // GENERIC: Store search_summary (product agent)
              ...(messageSearchSummary && { search_summary: messageSearchSummary }),
              ...(routingMetadataRef.current && { routingMetadata: routingMetadataRef.current })
            };
            
            // Debug: Confirm status flag is cleared on completion
            console.log('✅ Final message created - isStatusMessage cleared:', {
              messageId: finalAgentMessage.id,
              wasStatus: agentMessage.isStatusMessage,
              nowStatus: finalAgentMessage.isStatusMessage,
              agentId: agent.id
            });
            
            // Track ChatResponse for analytics - captures ACTUAL handling agent (Aldo handoffs)
            const routing = finalAgentMessage.a2aRouting;
            const routingMetadata = finalAgentMessage.routingMetadata || routingMetadataRef.current;
            
            // Prefer explicit routing metadata (selected_agent_id) when available
            const handlingAgentId = routingMetadata?.selected_agent_id ||
              routingMetadata?.selectedAgentId ||
              routing?.currentAgent ||
              agent.id;
            const handlingAgentName = routingMetadata?.selected_agent_name ||
              routing?.currentAgent ||
              agent.name;
            const selectedAgentId = agent.id;
            const selectedAgentName = agent.name;
            const wasRouted = handlingAgentId !== selectedAgentId && handlingAgentName !== selectedAgentName;
            
            // Track ChatResponse with defensive error handling and explicit flush
            try {
              applicationInsights.trackEvent('ChatResponse', {
                // The agent that ACTUALLY handled this request
                handlingAgent: handlingAgentId || handlingAgentName,
                handlingAgentId: handlingAgentId,
                handlingAgentName: handlingAgentName,
                // The agent the user originally selected
                selectedAgent: selectedAgentId,
                selectedAgentId: selectedAgentId,
                selectedAgentName: selectedAgentName,
                // Whether the request was routed to a different agent
                wasRouted: wasRouted ? 'true' : 'false',
                // Session info for user counting
                sessionId: sessionId,
                userId: userId,
                // Message metadata
                messageId: finalAgentMessage.id,
                contentLength: finalAgentMessage.content?.length?.toString() || '0',
                wasStreamed: 'true'
              });
              
              // Flush immediately to ensure event is sent before any cleanup
              applicationInsights.flush();
              
              console.log('📊 Tracked ChatResponse:', {
                handlingAgentId,
                handlingAgentName,
                selectedAgent: agent.id,
                wasRouted,
                appInsightsInitialized: applicationInsights.initialized
              });
            } catch (trackingError) {
              console.error('❌ Failed to track ChatResponse:', trackingError);
            }
            
            console.log('📝 Final agent message created:', {
              agentId: agent.id,
              messageId: finalAgentMessage.id,
              hasCitations: !!finalAgentMessage.citations,
              citationCount: finalAgentMessage.citations?.length || 0,
              hasCitationList: !!finalAgentMessage.citation_list,
              citationListLength: finalAgentMessage.citation_list?.length,
              contentLength: finalAgentMessage.content?.length,
              contentPreview: finalAgentMessage.content?.substring(0, 200)
            });
            
            // Debug logging for A2A routing preservation in real streaming completion
            // Log removed
            const finalConversation = messagesWithAgent.map(msg => 
              msg.id === finalAgentMessage.id ? finalAgentMessage : msg
            );
            onConversationUpdate(finalConversation);
            routingMetadataRef.current = null;
          },
          (error) => {
            if (!isStreamActive || currentStreamTokenRef.current !== streamToken) {
              return;
            }
            setIsStreaming(false);
            setCurrentStreamingMessage('');
            const isAbort = (error as any)?.name === 'AbortError' || ((error as any)?.status === 408);
            const suppress = Date.now() <= cancelSuppressUntilRef.current;
            // Clear any pending product display batches on error
            try {
              pendingProductDisplaysRef.current = {};
              if (productDisplayBatchTimeout.current) {
                clearTimeout(productDisplayBatchTimeout.current);
                productDisplayBatchTimeout.current = null;
              }
            } catch {}
            if (isAbort && suppress) {
              setStreamingMessageId(null);
              currentStreamTokenRef.current = null;
              isStreamActive = false;
              return;
            }
            console.error('⛔ useChat.sendMessage ERROR', {
              name: (error as any)?.name,
              message: (error as any)?.message,
              status: (error as any)?.status
            });
            currentStreamTokenRef.current = null;
            isStreamActive = false;
            throw error;
          },
          userId,
          false,
          messageContext
        );
        
      } else if (shouldUseStreaming) {
        // Handle streaming with simulation (when user wants streaming)
        // Log removed
const response = await apiService.sendConversationMessage(
          agent.id,
          conversationHistory,
          sessionId,
          userId,
          false
        );

        let responseContent = '';
        
        if (response.messages && Array.isArray(response.messages)) {
          const lastMessage = response.messages[response.messages.length - 1];
          responseContent = lastMessage?.content || '';
        } else if (response.response) {
          responseContent = response.response;
        }

        if (responseContent) {
          // Process citations for simulated streaming
          let processedContent = responseContent;
          
          // Create the agent message - thinking message for all agents
          const agentMessage: Message = {
            id: generateMessageId(),
            content: '🔍 Searching document library...',
            sender: 'agent',
            timestamp: new Date(),
            agentName: agent.name,
            wasStreamed: true,
            isCompleted: false,
            ...(response.citations && { citations: response.citations }),
            ...(response.citation_list && { citation_list: response.citation_list })
          };

          // Add message to conversation immediately
          const messagesWithAgent = [...updatedMessages, agentMessage];
          onConversationUpdate(messagesWithAgent);

          setIsStreaming(true);
          const streamToken = generateMessageId();
          currentStreamTokenRef.current = streamToken;
          
          // Reset metadata capture for this regenerated message
          streamingMetadataRef.current = null;

          // Simulate streaming
          await apiService.simulateStreamingResponse(
            processedContent,
            (currentText) => {
              if (currentStreamTokenRef.current !== streamToken) {
                return;
              }
              // For simulated streaming, just use the current text as-is
              // No need for additional processing since content is already formatted
              setCurrentStreamingMessage(currentText);
              
              // Update the agent message in the conversation
              const updatedAgentMessage = { 
                ...agentMessage, 
                content: currentText, 
                wasStreamed: true, 
                isCompleted: false,
                // Preserve existing A2A routing information during simulated streaming updates
                ...(agentMessage.a2aRouting && { a2aRouting: agentMessage.a2aRouting })
              };
              const updatedConversation = [...updatedMessages, updatedAgentMessage];
              onConversationUpdate(updatedConversation);
            },
            () => {
              if (currentStreamTokenRef.current !== streamToken) {
                return;
              }
              // Smooth transition for simulated streaming
              setTimeout(() => {
                setIsStreaming(false);
                setCurrentStreamingMessage('');
                
                // Brief delay to let streaming animation complete, then go directly to final state
                setTimeout(() => {
                  const finalAgentMessage = { 
                    ...agentMessage, 
                    content: processedContent, 
                    wasStreamed: true, 
                    isCompleted: true,
                    // Explicitly preserve A2A routing information for simulated streaming
                    a2aRouting: agentMessage.a2aRouting
                  };
                  
                  // Debug logging for A2A routing preservation in simulated streaming completion
                  // Log removed
const finalConversation = messagesWithAgent.map(msg => 
                    msg.id === finalAgentMessage.id ? finalAgentMessage : msg
                  );
                  onConversationUpdate(finalConversation);
                  currentStreamTokenRef.current = null;
                }, 100); // Very brief delay to let streaming animation finish
              }, 200); // Shorter delay for simulated streaming
            },
            40 // 40ms delay between words (smoother streaming animation)
          );
        }
      } else {
        // Handle instant responses (when user wants no streaming)
        // Log removed
// Show thinking animation even for instant responses
        const thinkingMessage: Message = {
          id: generateMessageId(),
          content: '🔍 Searching document library...',
          sender: 'agent',
          timestamp: new Date(),
          agentName: agent.name,
          wasStreamed: false,
          isCompleted: false
        };

        // Add thinking message to conversation immediately
        const messagesWithThinking = [...updatedMessages, thinkingMessage];
        onConversationUpdate(messagesWithThinking);
        
        const response = await apiService.sendConversationMessage(
          agent.id,
          conversationHistory,
          sessionId,
          userId,
          false
        );

        let responseContent = '';
        
        if (response.messages && Array.isArray(response.messages)) {
          const lastMessage = response.messages[response.messages.length - 1];
          responseContent = lastMessage?.content || '';
        } else if (response.response) {
          responseContent = response.response;
        }

        if (responseContent) {
          // Process citations for instant responses
          let processedContent = responseContent;
          
          // Update with final response content
          const finalMessage: Message = {
            ...thinkingMessage,
            content: processedContent,
            wasStreamed: false,
            isCompleted: true,
            ...(response.citations && { citations: response.citations }),
            ...(response.citation_list && { citation_list: response.citation_list })
          };

          // Replace thinking message with final response
          const finalConversation = messagesWithThinking.map(msg => 
            msg.id === finalMessage.id ? finalMessage : msg
          );
          onConversationUpdate(finalConversation);
          
          // TEMP: Track ChatResponse for non-streaming responses
          const routing = (finalMessage as any).a2aRouting;
          const actualAgent = routing?.currentAgent || agent.id;
          const wasRouted = routing && routing.currentAgent !== routing.originalAgent;
          
          applicationInsights.trackEvent('ChatResponse', {
            handlingAgent: actualAgent,
            handlingAgentName: routing?.currentAgent || agent.name,
            selectedAgent: routing?.originalAgent || agent.id,
            selectedAgentName: agent.name,
            wasRouted: wasRouted ? 'true' : 'false',
            sessionId: sessionId,
            userId: userId,
            messageId: finalMessage.id,
            contentLength: finalMessage.content?.length?.toString() || '0',
            wasStreamed: 'false'
          });
        }
      }

    } catch (error) {
      console.error('Error sending message:', error);
      // Suppress errors that occur immediately after an intentional cancel (click-away)
      if (Date.now() <= cancelSuppressUntilRef.current) {
        onConversationUpdate(existingMessages);
        return;
      }
      
      // Handle timeout errors with retry for filtering agents
      if (error instanceof ApiError && 
          error.status === 503 && 
          error.message.includes('timeout') &&
          agent.id.includes('filtering') &&
          retryCount < 1) {
        
        console.log(`Retrying filtering agent ${agent.id} (attempt ${retryCount + 1})`);
        setError('Agent is taking longer than expected. Retrying...');
        
        setTimeout(() => {
          sendMessage(content, agent, existingMessages, onConversationUpdate, streamingPreference, retryCount + 1);
        }, 2000);
        
        return;
      }
      
      // Show user-friendly error messages
      let errorMessage = 'Unable to send message. Please try again.';
      
      if (error instanceof ApiError) {
        if (error.status === 503 && error.message.includes('timeout')) {
          errorMessage = `The ${agent.name} is taking longer than expected to respond. This may be due to high processing requirements. Please try again or select a different agent.`;
        } else if (error.status === 503) {
          errorMessage = 'Agent service is temporarily unavailable. Please try again in a moment.';
        } else if (error.status >= 500) {
          errorMessage = 'Internal server error. Please try again.';
        }
      }
      
      setError(errorMessage);
      onConversationUpdate(existingMessages);
    } finally {
      // Log removed
setIsAgentTyping(false);
    }
  }, [sessionId, userId, generateMessageId, convertMessagesToConversation, currentStreamingMessage, processResponseContent, processStreamingContent]);

  // Cancel any active real-time streams and reset streaming state
  const cancelActiveStream = useCallback(() => {
    // Suppress abort/timeout errors for a short window after cancellation
    cancelSuppressUntilRef.current = Date.now() + 3000;
    try {
      apiService.cancelAllStreams();
    } catch {}
    setIsStreaming(false);
    setCurrentStreamingMessage('');
    setStreamingMessageId(null);
    currentStreamTokenRef.current = null;
    try {
      pendingProductDisplaysRef.current = {};
      if (productDisplayBatchTimeout.current) {
        clearTimeout(productDisplayBatchTimeout.current);
        productDisplayBatchTimeout.current = null;
      }
    } catch {}
  }, []);

  // Regenerate the last agent response with optional additional context
  const regenerateMessage = useCallback(async (
    messageId: string,
    agent: Agent,
    existingMessages: Message[],
    onConversationUpdate: (messages: Message[]) => void,
    streamingPreference: boolean = false,
    additionalContext?: string
  ) => {
    if (!agent) {
      console.error('❌ No agent provided');
      return;
    }

    setError(null);
    setToolCalls([]);
    setCurrentToolCall(null);
    setCurrentStreamingMessage('');

    // Find the message to regenerate and the previous user message
    const messageIndex = existingMessages.findIndex(msg => msg.id === messageId);
    
    if (messageIndex === -1 || existingMessages[messageIndex].sender !== 'agent') {
      console.error('❌ Cannot find agent message to regenerate');
      setError('Cannot regenerate this message');
      return;
    }

    const userMessageIndex = messageIndex - 1;
    
    if (userMessageIndex < 0 || existingMessages[userMessageIndex].sender !== 'user') {
      console.error('❌ Cannot find original user message');
      setError('Cannot find the original user message');
      return;
    }

    const originalUserMessage = existingMessages[userMessageIndex];
    
    // Create enhanced prompt if additional context provided
    let enhancedContent = originalUserMessage.content;
    if (additionalContext?.trim()) {
      enhancedContent = `${originalUserMessage.content}\n\nAdditional instructions: ${additionalContext.trim()}`;
    }

    // Remove the agent message we're regenerating and any messages after it
    const messagesBeforeRegeneration = existingMessages.slice(0, messageIndex);

    // Create a temporary enhanced user message for the API call
    const enhancedUserMessage: Message = {
      ...originalUserMessage,
      content: enhancedContent,
      id: generateMessageId()
    };

    const messagesForRegeneration = [...messagesBeforeRegeneration, enhancedUserMessage];

    // Let Aldo handle routing decisions naturally during regeneration - no proactive detection needed

    // Update conversation without the regenerated message
    onConversationUpdate(messagesBeforeRegeneration);

    // Log removed
setIsAgentTyping(true);

    try {
      const conversationHistory = convertMessagesToConversation(messagesForRegeneration);
      const shouldUseStreaming = streamingPreference; // Use streaming preference directly
      const isRealStreamingAgent = apiService.isRealStreamingAgent(agent.id);

      if (isRealStreamingAgent) {
        // Handle real streaming regeneration for webapp agent
        // Log removed
// Use default regeneration message
        const initialContent = '🔄 Regenerating response...';
        
        const regeneratedMessage: Message = {
          id: generateMessageId(),
          content: initialContent,
          sender: 'agent',
          timestamp: new Date(),
          agentName: agent.name,
          wasStreamed: true,
          isCompleted: false
        };

        const messagesWithRegenerated = [...messagesBeforeRegeneration, regeneratedMessage];
        onConversationUpdate(messagesWithRegenerated);

        setIsStreaming(true);
        let fullResponse = '';
        let regenerationCitationList = '';
        let regenerationCitationSourceChunks: any[] = [];
        // Store A2A routing data locally to preserve it in final completion
        let regenerationA2aRoutingData: any = null;
        let regenerationA2aToolCalls: string[] = [];
        let regenerationIsA2AMode = false;
        // Track current A2A routing for the regenerated message (updated by agent_switch events)
        let currentRegenerationA2aRouting: any = regeneratedMessage.a2aRouting || null;
        // Preserve the routed agent name throughout regeneration streaming
        let regenerationRoutedAgentName: string | undefined = undefined;
        
        // Reset metadata capture for this regenerated message
        streamingMetadataRef.current = null;

        await apiService.sendStreamingConversationMessage(
          agent.id, // Use agent for regeneration
          conversationHistory,
          sessionId,
          async (event) => {
            if (event.type === 'response' && event.content) {
              // Track timing of content events vs agent switch events
              console.log('📝 Content Event Received:', {
                contentLength: event.content.length,
                fullResponseLength: fullResponse.length,
                hasExistingA2ARouting: !!regeneratedMessage.a2aRouting,
                timestamp: Date.now()
              });
              
              fullResponse += event.content;
              
              // Process regeneration response content (no citations yet)
              const processedContent = processResponseContent(fullResponse);
              setCurrentStreamingMessage(processedContent);
              
              // Clear tool call status and status message flag when response starts
              if (fullResponse.trim()) {
                setCurrentToolCall(null);
                // Clear the status message flag once real content arrives
                regeneratedMessage.isStatusMessage = false;
              }
              
              // Note: Removed A2A content detection logic in regeneration as it was causing routing conflicts.
              // The frontend now relies solely on backend agent_switch events for routing information.
              
              // Update the agent message in the conversation with processed content during regeneration streaming
              // Apply citations to message object for styling, but don't modify content during streaming
              let citations: any[] = [];
              if (regenerationCitationSourceChunks && regenerationCitationSourceChunks.length > 0) {
                citations = regenerationCitationSourceChunks.map((chunk) => ({
                  citation_id: chunk.citation_id,
                  filename: chunk.filename || '',
                  page_number: chunk.page_number || 1,
                  chunk_index: chunk.chunk_index || 0,
                  content: chunk.content || '',
                  metadata: chunk.metadata || {},
                  search_score: chunk.search_score || 0,
                  citation: chunk.citation || ''
                }));
              }

              // Only replace regenerating message when we have meaningful content
              // Keep the original regenerating message if the processed content is empty or too short
              const shouldKeepRegeneratingMessage = !processedContent.trim() || processedContent.trim().length < 10;
              const displayContent = shouldKeepRegeneratingMessage ? regeneratedMessage.content : processedContent;

              // Defensive approach: preserve routed agent name once set (regeneration)
              const preservedRegenerationAgentName = regenerationRoutedAgentName || regeneratedMessage.agentName;

              let updatedMessage = { 
                ...regeneratedMessage, 
                content: displayContent, 
                wasStreamed: true, 
                isCompleted: false,
                isStatusMessage: false, // Clear status message flag when real content arrives
                // Apply citations for styling during streaming, but don't modify content
                ...(citations.length > 0 && { citations }),
                ...(regenerationCitationList && { citation_list: regenerationCitationList }),
                // Preserve A2A routing information - prioritize current routing, fallback to original
                a2aRouting: currentRegenerationA2aRouting || regeneratedMessage.a2aRouting,
                // Defensive preservation: ensure routed agent name is never lost once set (regeneration)
                ...(preservedRegenerationAgentName && { agentName: preservedRegenerationAgentName })
              };
              
              // Debug: Log status message flag clearing (regeneration)
              if (regeneratedMessage.isStatusMessage) {
                console.log('🔄 Clearing isStatusMessage flag for regenerated message', {
                  messageId: updatedMessage.id,
                  wasStatus: regeneratedMessage.isStatusMessage,
                  nowStatus: updatedMessage.isStatusMessage,
                  content: displayContent.substring(0, 50)
                });
              }
              
              // Debug logging for A2A routing preservation during regeneration content updates
              // Log removed
// Note: Removed hardcoded content analysis logic in regeneration flow that was overriding 
              // backend routing decisions. The frontend should only trust agent_switch events from the backend.
              
              const updatedConversation = [...messagesBeforeRegeneration, updatedMessage];
              onConversationUpdate(updatedConversation);
            } else if (event.type === 'agent_switch') {
              // Handle A2A agent switching - this confirms our routing worked
              const toAgentId = event.to_agent as string;
              const previousAgentName = event.previous_agent || event.from_agent;
              
              // Look up the target agent's display name with robust fallback
              // 1. Try to find in available agents list (from agents.json)
              const targetAgent = availableAgents?.find(a => a.id === toAgentId);
              // 2. Fall back to static mapping if agent list lookup fails
              const fallbackName = AGENT_NAME_FALLBACK[toAgentId];
              // 3. Last resort: use the agent ID itself
              const newAgentName = targetAgent?.name || fallbackName || toAgentId;
              
              // Track timing of agent switch events
              console.log('🔄 Agent Switch Event Received (Regeneration):', {
                toAgentId,
                targetAgent: targetAgent?.name,
                fallbackName,
                newAgentName,
                previousAgentName,
                availableAgentsCount: availableAgents?.length || 0,
                fullResponseLength: fullResponse.length,
                timestamp: Date.now()
              });
              
              if (newAgentName) {
                // Notify consumer for regeneration switches as well
                try { onAgentSwitch && onAgentSwitch(newAgentName as string, toAgentId, previousAgentName); } catch {}
                console.log(`🔄 A2A routing confirmed via agent_switch event (regeneration): ${previousAgentName || agent.name} → ${newAgentName}`);
                
                // Set A2A mode flag for regeneration to capture subsequent tool calls
                regenerationIsA2AMode = true;
                console.log('🎯 A2A mode activated (regeneration) - will capture target agent tool calls');
                
                // Status messages are disabled - skip status message logic
                // Log removed
// Update the agent message to show the switch
                // Always add A2A routing metadata for Aldo agent to show routing indicators
                // Preserve thinking message until we have substantial content
                let switchMessage = fullResponse.length > 0 ? fullResponse : regeneratedMessage.content;
                
                // Only show routing message if we have substantial response content, otherwise keep thinking message
                if (fullResponse.length < 10) {
                  switchMessage = regeneratedMessage.content; // Keep the original regenerating message
                }
                
                // Always add A2A routing metadata for Aldo agent (agent.id === 'aldo')
                if (agent.id === 'aldo') {
                  // Normal A2A routing - add routing metadata
                  const routingData = {
                    originalAgent: agent.name,
                    currentAgent: newAgentName,
                    previousAgent: previousAgentName
                  };
                  
                  const updatedAgentMessage = { 
                    ...regeneratedMessage, 
                    content: switchMessage, 
                    wasStreamed: true, 
                    isCompleted: false,
                    agentName: newAgentName, // Update to routed agent name for immediate display
                    a2aRouting: routingData
                  };
                  
                  // Store A2A routing data and agent name locally to preserve throughout regeneration streaming
                  regenerationA2aRoutingData = routingData;
                  regenerationRoutedAgentName = newAgentName;
                  
                  // Update current A2A routing for subsequent content updates
                  currentRegenerationA2aRouting = updatedAgentMessage.a2aRouting;
                  
                  const updatedConversation = [...messagesBeforeRegeneration, updatedAgentMessage];
                  onConversationUpdate(updatedConversation);
                } else {
                  // Not Aldo agent - just update content without A2A metadata
                  // Log removed
const updatedAgentMessage = { 
                    ...regeneratedMessage, 
                    content: switchMessage, 
                    wasStreamed: true, 
                    isCompleted: false,
                    agentName: agent.name // Keep original agent name instead of target agent
                    // No a2aRouting metadata
                  };
                  const updatedConversation = [...messagesBeforeRegeneration, updatedAgentMessage];
                  onConversationUpdate(updatedConversation);
                }
              }
            } else if (event.type === 'tool_call' && event.tool_name) {
              // Track the current tool being called
              setCurrentToolCall(event.tool_name);
              
              // Add tool call to appropriate list based on A2A mode (regeneration)
              if (regenerationIsA2AMode) {
                // This is a tool call during A2A routing in regeneration
                if (!regenerationA2aToolCalls.includes(event.tool_name)) {
                  regenerationA2aToolCalls.push(event.tool_name);
                  console.log('🔧 A2A tool call captured (regeneration, tool_call event):', event.tool_name);
                }
              } else {
                // Regular tool call during regeneration - add to toolCalls state
                const newToolCall: ToolCall = {
                  id: `tool_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  name: event.tool_name,
                  input: event.tool_input || {},
                  status: 'calling',
                  timestamp: new Date()
                };
                setToolCalls(prev => [...prev, newToolCall]);
              }
            } else if (event.type === 'status') {
              // Handle dynamic status updates from product microagent and other agents
              if (event.status && typeof event.status === 'string') {
                // Use status_message for user display if available, otherwise use status
                const displayMessage = (event as any).status_message || event.status;
                
                // Skip internal status messages (like 'agent_updated', 'tool_completed') - don't display or update message content
                if (event.status !== 'agent_updated' && event.status !== 'tool_completed') {
                  // Display status message to user
                  if (onStatusMessage) {
                    onStatusMessage(displayMessage);
                  }
                  
                  // Update the regenerating message content to show current status
                  if (!regeneratedMessage.isCompleted) {
                    const statusUpdatedMessage = { 
                      ...regeneratedMessage, 
                      content: displayMessage,
                      isCompleted: false,
                      isStatusMessage: true
                    };
                    const updatedConversation = [...messagesBeforeRegeneration, statusUpdatedMessage];
                    onConversationUpdate(updatedConversation);
                  }
                } else {
                  // Log agent_updated events for debugging but don't display (regeneration)
                  console.log('Skipping agent_updated status display (regeneration)');
                }
              }
              
              // Handle tool calling via status events in regeneration (common in A2A routing)
              if (event.status === 'tool_calling' && event.tool_name) {
                setCurrentToolCall(event.tool_name);
                
                if (regenerationIsA2AMode) {
                  // A2A tool call during regeneration
                  if (!regenerationA2aToolCalls.includes(event.tool_name)) {
                    regenerationA2aToolCalls.push(event.tool_name);
                    console.log('🔧 A2A tool call captured (regeneration, status event):', event.tool_name);
                  }
                } else {
                  // Regular tool call via status event during regeneration
                  const newToolCall: ToolCall = {
                    id: `tool_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    name: event.tool_name,
                    input: {},
                    status: 'calling',
                    timestamp: new Date()
                  };
                  setToolCalls(prev => [...prev, newToolCall]);
                }
              }
            } else if (event.type === 'citation_list' && event.citation_list) {
              // Accumulate citation list for regeneration but don't apply to content yet
              regenerationCitationList = event.citation_list;
              // Log removed
} else if (event.type === 'citation_source_chunks' && event.source_chunks) {
              // Accumulate citation source chunks for regeneration
              regenerationCitationSourceChunks = event.source_chunks;
              // Log removed
} else if (event.type === 'done' && event.metadata) {
              // GENERIC: Capture metadata from backend completion event (regeneration path)
              streamingMetadataRef.current = event.metadata;
              console.log('📊 Captured metadata from backend (regeneration):', {
                hasMetadata: !!event.metadata,
                metadataKeys: Object.keys(event.metadata || {})
              });
            } else if (event.type === 'error') {
              // Same silent-drop bug as the primary stream (see other onEvent
              // handler above) — surface it instead of leaving regeneration
              // stuck with no content and no feedback.
              const backendError = (event as any).error || 'The assistant ran into a problem generating a response.';
              const backendRequestId = (event as any).requestId;
              console.error('❌ SSE stream error event (regeneration):', { error: backendError, requestId: backendRequestId, event });
              setError(backendRequestId ? `${backendError} (ref: ${backendRequestId.slice(0, 8)})` : backendError);
              setIsStreaming(false);
              setCurrentStreamingMessage('');
            }
          },
          (finalResponse) => {
            // Since we're processing content in real-time, just complete the streaming
            setIsStreaming(false);
            setCurrentStreamingMessage('');
            
            // Citations are already applied during streaming, so just mark as completed
            // No setTimeout needed - this prevents the scroll jump issue
            
            // Convert citation source chunks to Citation objects if not already done
            let citations: any[] = [];
            if (regenerationCitationSourceChunks && regenerationCitationSourceChunks.length > 0) {
              citations = regenerationCitationSourceChunks.map((chunk) => ({
                citation_id: chunk.citation_id,
                filename: chunk.filename || '',
                page_number: chunk.page_number || 1,
                chunk_index: chunk.chunk_index || 0,
                content: chunk.content || '',
                metadata: chunk.metadata || {},
                search_score: chunk.search_score || 0,
                citation: chunk.citation || '',
                // Preserve image data for image replacement
                image_ids: chunk.image_ids || [],
                image_urls: chunk.image_urls || [],
                image_captions: chunk.image_captions || [],
                image_formats: chunk.image_formats || [],
                image_sizes: chunk.image_sizes || []
              }));
            }
            
            // Process final regeneration content with citations consistently
            const finalContent = currentStreamingMessage 
              ? processStreamingContent(currentStreamingMessage, regenerationCitationList, citations)
              : processStreamingContent(finalResponse, regenerationCitationList, citations);
            
            const finalMessage = { 
              ...regeneratedMessage,
              content: finalContent, 
              wasStreamed: true, 
              isCompleted: true,
              isStatusMessage: false, // CRITICAL: Clear status message flag on completion
              ...(citations.length > 0 && { citations }),
              // Only add citation_list if it wasn't already included in content
              ...(!finalContent.toLowerCase().includes('sources') && regenerationCitationList && { citation_list: regenerationCitationList }),
              // Preserve A2A routing data - prioritize local variable, fallback to regenerated message
              a2aRouting: regenerationA2aRoutingData || regeneratedMessage.a2aRouting,
              // Preserve routed agent name - prioritize local variable, fallback to regenerated message
              ...(regenerationRoutedAgentName && { agentName: regenerationRoutedAgentName }),
              // Store A2A tool calls for feedback (regeneration)
              ...(regenerationA2aToolCalls.length > 0 && { a2aToolCalls: regenerationA2aToolCalls })
            };
            
            // Debug: Confirm status flag is cleared on completion (regeneration)
            console.log('✅ Regenerated message completed - isStatusMessage cleared:', {
              messageId: finalMessage.id,
              wasStatus: regeneratedMessage.isStatusMessage,
              nowStatus: finalMessage.isStatusMessage,
              agentId: agent?.id
            });
            
            // Debug logging for A2A routing preservation in regeneration completion
            // Log removed
const finalConversation = [...messagesBeforeRegeneration, finalMessage];
            onConversationUpdate(finalConversation); // Very brief delay to let streaming animation finish
          },
          (error) => {
            setIsStreaming(false);
            setCurrentStreamingMessage('');
            const isAbort = (error as any)?.name === 'AbortError' || ((error as any)?.status === 408);
            const suppress = Date.now() <= cancelSuppressUntilRef.current;
            if (isAbort && suppress) {
              setStreamingMessageId(null);
              return;
            }
            setStreamingMessageId(null);
            throw error;
          },
          userId
        );
        
      } else if (shouldUseStreaming) {
        // Handle streaming regeneration with simulation
        // Log removed
const response = await apiService.sendConversationMessage(
          agent.id,
          conversationHistory,
          sessionId,
          userId,
          true
        );

        let responseContent = '';
        
        if (response.messages && Array.isArray(response.messages)) {
          const lastMessage = response.messages[response.messages.length - 1];
          responseContent = lastMessage?.content || '';
        } else if (response.response) {
          responseContent = response.response;
        }

        if (responseContent) {
          // Process citations for simulated streaming regeneration
          let processedContent = responseContent;
          
          const regeneratedMessage: Message = {
            id: generateMessageId(),
            content: '🔄 Regenerating response...',
            sender: 'agent',
            timestamp: new Date(),
            agentName: agent.name,
            wasStreamed: true,
            isCompleted: false,
            ...(response.citations && { citations: response.citations }),
            ...(response.citation_list && { citation_list: response.citation_list })
          };

          const messagesWithRegenerated = [...messagesBeforeRegeneration, regeneratedMessage];
          onConversationUpdate(messagesWithRegenerated);

          setIsStreaming(true);

          // Simulate streaming for regeneration
          await apiService.simulateStreamingResponse(
            processedContent,
            (currentText) => {
              // Process simulated regeneration content in real-time for consistency
              const citations = response.citations || [];
              const realtimeProcessedContent = processStreamingContent(currentText, response.citation_list, citations);
              setCurrentStreamingMessage(realtimeProcessedContent);
              
              const updatedMessage = { 
                ...regeneratedMessage, 
                content: realtimeProcessedContent, 
                wasStreamed: true, 
                isCompleted: false
                // Note: A2A routing is already preserved by spreading regeneratedMessage
              };
              const updatedConversation = [...messagesBeforeRegeneration, updatedMessage];
              onConversationUpdate(updatedConversation);
            },
            () => {
              // Smooth transition for simulated regeneration streaming
              setTimeout(() => {
                setIsStreaming(false);
                setCurrentStreamingMessage('');
                
                // Brief delay to let streaming animation complete, then go directly to final state
                setTimeout(() => {
                  const finalAgentMessage = { 
                    ...regeneratedMessage, 
                    content: processedContent, 
                    wasStreamed: true, 
                    isCompleted: true,
                    // Explicitly preserve A2A routing information for simulated regeneration
                    a2aRouting: regeneratedMessage.a2aRouting
                  };
                  
                  // Debug logging for A2A routing preservation in simulated regeneration completion
                  // Log removed
const finalConversation = messagesWithRegenerated.map(msg => 
                    msg.id === finalAgentMessage.id ? finalAgentMessage : msg
                  );
                  onConversationUpdate(finalConversation);
                }, 100); // Very brief delay to let streaming animation finish
              }, 200); // Shorter delay for simulated streaming
            },
            40 // 40ms delay between words (smoother streaming animation)
          );
        }
      } else {
        // Handle instant regeneration (no streaming)
        // Log removed
// Show regenerating animation even for instant responses
        const regeneratingMessage: Message = {
          id: generateMessageId(),
          content: '🔄 Regenerating response...',
          sender: 'agent',
          timestamp: new Date(),
          agentName: agent.name,
          wasStreamed: false,
          isCompleted: false
        };

        // Add regenerating message to conversation immediately
        const messagesWithRegenerating = [...messagesBeforeRegeneration, regeneratingMessage];
        onConversationUpdate(messagesWithRegenerating);
        
        const response = await apiService.sendConversationMessage(
          agent.id,
          conversationHistory,
          sessionId,
          userId,
          true
        );

        let responseContent = '';
        
        if (response.messages && Array.isArray(response.messages)) {
          const lastMessage = response.messages[response.messages.length - 1];
          responseContent = lastMessage?.content || '';
        } else if (response.response) {
          responseContent = response.response;
        }

        if (responseContent) {
          // Process citations for instant regeneration responses
          let processedContent = responseContent;
          
          // Update with final response content
          const finalMessage: Message = {
            ...regeneratingMessage,
            content: processedContent,
            wasStreamed: false,
            isCompleted: true,
            ...(response.citations && { citations: response.citations }),
            ...(response.citation_list && { citation_list: response.citation_list })
          };

          // Replace regenerating message with final response
          const finalConversation = [...messagesBeforeRegeneration, finalMessage];
          onConversationUpdate(finalConversation);
        }
      }

    } catch (error) {
      console.error('❌ Error regenerating message:', error);
      // Suppress errors that occur immediately after an intentional cancel (click-away)
      if (Date.now() <= cancelSuppressUntilRef.current) {
        onConversationUpdate(messagesBeforeRegeneration);
        return;
      }
      
      let errorMessage = 'Unable to regenerate message. Please try again.';
      
      if (error instanceof ApiError) {
        if (error.status === 503 && error.message.includes('timeout')) {
          errorMessage = `The ${agent.name} is taking longer than expected to respond. Please try again.`;
        } else if (error.status === 503) {
          errorMessage = 'Agent service is temporarily unavailable. Please try again in a moment.';
        } else if (error.status >= 500) {
          errorMessage = 'Internal server error. Please try again.';
        }
      }
      
      setError(errorMessage);
      onConversationUpdate(messagesBeforeRegeneration);
    } finally {
      // Log removed
setIsAgentTyping(false);
    }
  }, [sessionId, userId, generateMessageId, convertMessagesToConversation, currentStreamingMessage, processResponseContent, processStreamingContent]);

  // GENERIC: Handle alternative selection (works for any agent)
  // This callback is intentionally simple - it just triggers the parent component's
  // onSendMessage which has the full context (agent, messages, etc.)
  const handleAlternativeSelect = useCallback((
    filters: Record<string, any>,
    description: string
  ) => {
    console.log('🎯 User selected alternative:', { description, filters });
    
    // Return the message and context - parent component will handle the actual send
    return {
      message: `Show me: ${description}`,
      context: {
        filterOverride: filters,
        isAlternativeSelection: true
      }
    };
  }, []);

  /**
   * Send a clarification response to resume from where we paused.
   * Supports both:
   * 1. Option selection: User picks from provided options
   * 2. Natural language: User provides freeform response
   */
  const sendClarificationResponse = useCallback(async (
    response: string,
    agent: Agent,
    messages: Message[],
    onConversationUpdate: (messages: Message[]) => void,
    selectedOption?: { value: string; field?: string }
  ) => {
    if (!clarificationState) {
      console.warn('No pending clarification to respond to');
      return;
    }

    console.log('📤 Sending clarification response:', {
      response,
      selectedOption,
      stage: clarificationState.stage,
      sessionId: clarificationState.sessionId
    });

    // Clear clarification state
    setPendingClarification(false);
    setClarificationState(null);

    // Build the message context with clarification info
    const messageContext = {
      clarification_response: response,
      clarification_selection: selectedOption || null
    };

    // Send as a regular message with clarification context
    // The backend will detect this is a resume and continue from the saved stage
    // Parameters: content, agent, messages, onConversationUpdate, streamingPreference, retryCount, messageContext
    await sendMessage(response, agent, messages, onConversationUpdate, false, 0, messageContext);
  }, [clarificationState, sendMessage]);

  /**
   * Clear pending clarification (e.g., if user wants to start fresh)
   */
  const clearClarification = useCallback(() => {
    setPendingClarification(false);
    setClarificationState(null);
  }, []);

  /**
   * Clear all product-related cache and state
   * Should be called when switching conversations to prevent stale data
   */
  const clearProductCache = useCallback(() => {
    console.log('🧹 Clearing product cache on conversation switch');
    
    // Clear product displays state and refs
    setProductDisplays({});
    setAlternativeDisplays({});
    productDisplaysRef.current = {};
    pendingProductDisplaysRef.current = {};
    
    // Clear any pending batch timeout
    if (productDisplayBatchTimeout.current) {
      clearTimeout(productDisplayBatchTimeout.current);
      productDisplayBatchTimeout.current = null;
    }
    
    // Clear filter alternatives and metadata refs
    filterAlternativesRef.current = null;
    streamingMetadataRef.current = null;
    routingMetadataRef.current = null;
    
    // Clear clarification state
    setPendingClarification(false);
    setClarificationState(null);
  }, []);

  return {
    sendMessage,
    regenerateMessage,
    isAgentTyping,
    isStreaming,
    currentStreamingMessage,
    toolCalls,
    currentToolCall,
    error,
    // Expose product display events keyed by anchor_id for inline rendering
    productDisplays,
    // Expose alternative displays keyed by anchor_id for [ALTERNATIVES: anchor_id] replacement
    alternativeDisplays,
    clearError: useCallback(() => setError(null), []),
    cancelActiveStream,
    handleAlternativeSelect,  // GENERIC: Callback for clickable alternatives
    // Clarification support
    clarificationState,
    pendingClarification,
    sendClarificationResponse,
    clearClarification,
    // Cache management
    clearProductCache  // Clear product displays/alternatives on conversation switch
  };
}; 